import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// Import team member images
import Humza from "../../images/Humza.webp";
import Kareem from "../../images/Kareem.webp";
import Yazan from "../../images/Yazan.webp";
import Deniz from "../../images/Deniz.webp";
import Ali from "../../images/Ali.webp";
import Asad from "../../images/Asad.webp";
import Hira from "../../images/Hira.webp";

// Import board member images
import Katharine from "../../images/Katharine.png";
import Aisha from "../../images/Aisha.jpg";
import Nabila from "../../images/Nabila.png";

// Import branch logos
import Carnegie from "../../images/SFR_Carnegie_Logo.svg";
import Bellaire from "../../images/SFR_Bellaire_Logo.svg";
import DeBakey from "../../images/SFR_DeBakey_Logo.svg";
import Tamu from "../../images/SFR_Tamu_Logo.svg";
import Challenge from "../../images/SFR_Challenge_Logo.svg";
import UH from "../../images/SFR_UH_Logo.svg";
import Westside from "../../images/SFR_Westside_Logo.svg";
import Homeschool from "../../images/SFR_Homeschool_Logo.svg";
import Baylor from "../../images/SFR_Baylor_Logo.svg";
import UT_Austin from "../../images/SFR_UT_Austin_Logo.svg";
import Jordan from "../../images/SFR_Jordan_Logo.svg";
import London from "../../images/SFR_London_Logo.svg";

function Team() {
  // Executive team members
  const executiveTeam = [
    { src: Humza, name: "Imran Humza Hanif", title: "President" },
    { src: Deniz, name: "Deniz Çalışkan", title: "Vice President" },
    { src: Hira, name: "Hira Malik", title: "Secretary" },
    { src: Ali, name: "Ali Faruqui", title: "Treasurer" },
    { src: Yazan, name: "Yazan Zarka", title: "International Relations" },
    { src: Asad, name: "As'ad Ansari", title: "Outreach Officer" },
    { src: Kareem, name: "Kareem Kabbani", title: "Web Developer" }
  ];

  // Board members
  const boardMembers = [
    {
      src: Katharine,
      name: "Katharine Gratwick",
      title: "Development Economist",
      bio: "Katharine Gratwick is a development economist living and working in Houston, TX. She specializes in sub-Saharan African electricity policy. She joined SFR in 2023 and is thrilled to learn and work alongside a group of committed and concerned individuals who believe our strength lies in our diversity, our compassion, and ultimately our ability to serve one another."
    },
    {
      src: Aisha,
      name: "Aisha Koroma",
      title: "Mental Health & Social Services Professional",
      bio: "Aisha Koroma, born in Sierra Leone, is a trauma-responsive mental health and social services professional dedicated to empowering marginalized populations. A war survivor and refugee, she migrated to the US and now aids refugee integration. A licensed therapist with degrees in psychology and counseling, she has spearheaded mental health initiatives, co-authored works on refugee experiences, and developed innovative GBV programs."
    },
    {
      src: Nabila,
      name: "Nabila Ansar",
      title: "Managing Attorney",
      bio: "Nabila is the Managing Attorney of the Law Office of Nabila Ansari. She assists her clients in resolving complex legal problems in the areas of business and corporate governance, risk mitigation and contracts. Nabila is a longtime Houston resident and has been active in supporting her community through volunteer work, including refugee support services, for many years. She joined the board in 2024."
    }
  ];

  // Active branches
  const branches = [
    { src: Carnegie, name: "Carnegie Vanguard" },
    { src: Bellaire, name: "Bellaire" },
    { src: DeBakey, name: "DeBakey" },
    { src: Baylor, name: "Baylor" },
    { src: Tamu, name: "Texas A&M" },
    { src: Challenge, name: "Challenge" },
    { src: UH, name: "University of Houston" },
    { src: Westside, name: "Westside" },
    { src: Homeschool, name: "Homeschool" },
    { src: UT_Austin, name: "UT Austin" },
    { src: Jordan, name: "Jordan" },
    { src: London, name: "London" }
  ];

  return (
    <section id="team" className="py-16 bg-offWhite overflow-hidden relative">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        {/* Executive Team Section */}
        <div className="text-center mb-12">
          <h2 className="font-display text-h2 text-secondary mb-2">OUR TEAM</h2>
          <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
            Meet the passionate people behind our mission to support refugee
            students worldwide
          </p>
          <div className="w-16 h-1 bg-cta mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {executiveTeam.map((member, index) => (
            <motion.div
              key={index}
              className="group flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="h-64 w-64 rounded-2xl bg-offWhite shadow-lg overflow-hidden">
                <img
                  src={member.src}
                  alt={member.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="font-display text-h3 text-secondary mt-4">
                {member.name}
              </h3>
              <p className="text-primary font-bold text-center">{member.title}</p>
              <div className="w-12 h-1 bg-cta mx-auto mt-2"></div>
            </motion.div>
          ))}
        </div>

        {/* Board Members Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="font-display text-h2 text-secondary mb-2">
              BOARD MEMBERS
            </h2>
            <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
              Meet our dedicated board who guide our organization's mission and
              vision
            </p>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {boardMembers.map((member, index) => (
            <motion.div
              key={index}
              className="group flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="h-64 w-64 rounded-2xl bg-offWhite shadow-lg overflow-hidden">
                <img
                  src={member.src}
                  alt={member.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="font-display text-h3 text-secondary mt-4">
                {member.name}
              </h3>
              <p className="text-primary font-bold text-center">{member.title}</p>
              <div className="w-12 h-1 bg-cta mx-auto mt-2"></div>
            </motion.div>
          ))}
          </div>
        </div>

        {/* Branches Section */}
        <div className="mb-12">
          <div className="text-center mb-12">
            <h2 className="font-display text-h2 text-secondary mb-2">
              ACTIVE BRANCHES
            </h2>
            <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
              Our presence across schools and universities
            </p>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
            {branches.map((branch, index) => (
              <motion.div
                key={index}
                className="group flex flex-col items-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -5 }}
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden bg-white shadow-lg flex items-center justify-center">
                  <img
                    src={branch.src}
                    className="w-full h-full object-contain"
                    alt={branch.name}
                  />
                </div>
                <p className="text-sm text-textPrimary text-center mt-2">
                  {branch.name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Background decorative elements */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary opacity-5 z-0"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-cta opacity-5 z-0"></div>
      </div>
    </section>
  );
}

export default Team;
